import { isSuccess } from '@/context/services/type';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import { useQueryClient } from '@tanstack/react-query';
import type {
  PostAgreementAiServiceMutationError,
  UpdateApPayeeAiServiceEnablementsMutationError,
  UpdateAiModelTrainingSettingMutationError,
  UpdateApAiServiceEnablementMutationError,
} from 'ap-openapi';
import {
  getGetApOfficeAiSettingQueryKey,
  useGetApOfficeAiSettingSuspense,
  usePostAgreementAiService,
  useUpdateAiModelTrainingSetting,
  useUpdateApPayeeAiServiceEnablements as useBaseUpdateApPayeeAiServiceEnablements,
  useUpdateApAiServiceEnablement,
} from 'ap-openapi';
import type { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import type { AiService, AiServiceSettingResponse } from './type';

export const useGetAiServiceSetting = (): AiServiceSettingResponse => {
  const { data } = useGetApOfficeAiSettingSuspense();

  return data.data;
};

export const useAgreeAiService = () => {
  const { mutateAsync: _mutateAsync, isPending } = usePostAgreementAiService();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutateAsync = useCallback(
    async (isModelTrainingAllowed: boolean) => {
      let response: AxiosResponse<void | PostAgreementAiServiceMutationError>;

      try {
        response = await _mutateAsync({
          data: {
            is_model_training_allowed: isModelTrainingAllowed,
          },
        });
      } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
      }

      if (isSuccess(response.status)) {
        await queryClient.invalidateQueries({
          queryKey: getGetApOfficeAiSettingQueryKey(),
        });
        return { isSuccessful: true } as const;
      }

      if (!response.data || !isCommonError(response.data)) {
        throw new Error(
          `Unexpected AI service agreement response shape: ${response.status}`
        );
      }

      const { messages } = response.data;

      return {
        isSuccessful: false,
        message: messages?.length
          ? messages.join(', ')
          : t('ai_service_setting.agreement_error'),
      } as const;
    },
    [_mutateAsync, queryClient, t]
  );

  return { isPending, mutateAsync };
};

export const useUpdateModelTrainingSetting = () => {
  const { mutateAsync: _mutateAsync, isPending } =
    useUpdateAiModelTrainingSetting();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutateAsync = useCallback(
    async (isAllowed: boolean) => {
      let response: AxiosResponse<void | UpdateAiModelTrainingSettingMutationError>;

      try {
        response = await _mutateAsync({
          data: {
            is_allowed: isAllowed,
          },
        });
      } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
      }

      if (isSuccess(response.status)) {
        await queryClient.invalidateQueries({
          queryKey: getGetApOfficeAiSettingQueryKey(),
        });
        return { isSuccessful: true } as const;
      }

      if (!response.data || !isCommonError(response.data)) {
        throw new Error(
          `Unexpected model training setting response shape: ${response.status}`
        );
      }

      const { messages } = response.data;

      return {
        isSuccessful: false,
        message: messages?.length
          ? messages.join(', ')
          : t('ai_service_setting.model_training.update_error'),
      } as const;
    },
    [_mutateAsync, queryClient, t]
  );

  return { isPending, mutateAsync };
};

export const useUpdateAiServiceEnablement = () => {
  const { mutateAsync: _mutateAsync, isPending } =
    useUpdateApAiServiceEnablement();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutateAsync = useCallback(
    async (service: AiService, isEnabled: boolean) => {
      let response: AxiosResponse<void | UpdateApAiServiceEnablementMutationError>;

      try {
        response = await _mutateAsync({
          serviceType: service.service_type,
          data: {
            is_enabled: isEnabled,
          },
        });
      } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
      }

      if (isSuccess(response.status)) {
        await queryClient.invalidateQueries({
          queryKey: getGetApOfficeAiSettingQueryKey(),
        });
        return { isSuccessful: true } as const;
      }

      if (!response.data || !isCommonError(response.data)) {
        throw new Error(
          `Unexpected updating ai service enablement response shape: ${response.status}`
        );
      }

      const { messages } = response.data;

      return {
        isSuccessful: false,
        message: messages?.length
          ? messages.join(', ')
          : t(
              isEnabled
                ? 'ai_service_setting.service_start_error'
                : 'ai_service_setting.service_stop_error',
              { serviceName: service.name }
            ),
      } as const;
    },
    [_mutateAsync, queryClient, t]
  );

  return { isPending, mutateAsync };
};

export const useUpdateApPayeeAiServiceEnablements = () => {
  const { mutateAsync: _mutateAsync, isPending } =
    useBaseUpdateApPayeeAiServiceEnablements();
  const { t } = useTranslation();

  const mutateAsync = useCallback(
    async (
      apPayeeId: string,
      aiServiceEnablements: Record<string, boolean>
    ) => {
      let response: AxiosResponse<void | UpdateApPayeeAiServiceEnablementsMutationError>;

      try {
        response = await _mutateAsync({
          id: apPayeeId,
          data: {
            ai_service_enablements: aiServiceEnablements,
          },
        });
      } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
      }

      if (isSuccess(response.status)) {
        return { isSuccessful: true } as const;
      }

      if (!response.data || !isCommonError(response.data)) {
        throw new Error(
          `Unexpected updating ap payee ai service enablements response shape: ${response.status}`
        );
      }

      const { messages } = response.data;

      return {
        isSuccessful: false,
        message: messages?.length
          ? messages.join(', ')
          : t('ai_service_setting.service_update_error'),
      } as const;
    },
    [_mutateAsync, t]
  );

  return { isPending, mutateAsync };
};
